import type { Conversation, Message } from '../api/types';

/**
 * Who an email message was addressed to, and who a reply to it should go to.
 *
 * The two directions carry the answer in different places, which is the whole
 * reason this is one module rather than a field read at each call site:
 *
 *  - an INBOUND mail carries the headers it arrived with, under
 *    `contentAttributes.email` (`to`, `cc`, `bcc` — see channels/email/parse.js);
 *  - an OUTBOUND one carries what the composer asked for, as the flat
 *    `toEmails` / `ccEmails` / `bccEmails` the send path reads
 *    (services/outbound.js). `toEmails` is ABSENT on an ordinary reply, because
 *    the server falls back to the contact's own address — so "no toEmails" means
 *    "the contact", not "nobody", and the caller has to supply that fallback.
 *
 * Everything here is pure and string-only: no react, no api, no formatting
 * decisions. The rendering side decides how much of a long list to show.
 */

/** Kept in step with utils/emailAddresses.js — the server refuses what this
 *  accepts, so a looser rule here would only move the error later. The middle
 *  group is the domain's optional inner labels: without it the rule meant
 *  "exactly one dot" and refused every subdomain, which is what put a valid
 *  `noreply@bounces.houzz.com` in the box wearing the invalid style. */
const ADDRESS = /^[^\s@<>,;"]+@[^\s@<>,;".]+(\.[^\s@<>,;".]+)*\.[^\s@<>,;".]{2,}$/;

export interface Recipients {
  to: string[];
  cc: string[];
  bcc: string[];
}

export const emptyRecipients = (): Recipients => ({ to: [], cc: [], bcc: [] });

/** `Bob <bob@acme.com>` / `bob@acme.com` -> `bob@acme.com`, or null. */
export function addressIn(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const angled = trimmed.match(/<([^<>]+)>\s*$/);
  const address = (angled ? angled[1] : trimmed).trim();
  return ADDRESS.test(address) ? address : null;
}

export const isAddress = (value: string): boolean => addressIn(value) !== null;

/** The display half, for a chip: `Bob <b@x>` -> `Bob`, `b@x` -> `b@x`. */
export function addressLabel(value: string): string {
  const trimmed = value.trim();
  const angled = trimmed.match(/^(.*?)\s*<[^<>]+>\s*$/);
  const name = angled?.[1]?.replace(/^["']|["']$/g, '').trim();
  return name || addressIn(trimmed) || trimmed;
}

/**
 * Split what somebody pasted into a recipient box. Commas, semicolons and
 * newlines all separate — an address list copied out of Outlook uses semicolons,
 * one copied out of a spreadsheet uses newlines — but a space does NOT, or
 * `Bob Smith <b@x>` would come apart into three things.
 */
export function splitAddresses(input: string): string[] {
  return input
    .split(/[,;\n\r\t]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Case-insensitive, and on the address only, so `Bob <b@x>` and `b@x` are one. */
const keyOf = (value: string): string => (addressIn(value) ?? value.trim()).toLowerCase();

/** Order-preserving dedup, optionally dropping addresses already spoken for. */
export function dedupeAddresses(values: string[], exclude: string[] = []): string[] {
  const seen = new Set(exclude.map(keyOf));
  const out: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const key = keyOf(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

const listOf = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

/**
 * The addresses a message went to or came from, in wire order.
 *
 * `fallbackTo` is the contact's own address, used only when an outgoing message
 * named no To of its own — which is every ordinary reply.
 */
export function recipientsOf(message: Message, fallbackTo?: string | null): Recipients {
  const attributes = message.contentAttributes as Record<string, unknown>;
  if (message.messageType === 'incoming') {
    const email = message.contentAttributes.email;
    return {
      to: listOf(email?.to),
      cc: listOf(email?.cc),
      // Inbound Bcc is all but always empty — a blind copy is stripped before it
      // reaches the recipient, so anything here was addressed to this mailbox.
      bcc: listOf(email?.bcc),
    };
  }
  const to = listOf(attributes.toEmails);
  return {
    to: to.length ? to : fallbackTo ? [fallbackTo] : [],
    cc: listOf(attributes.ccEmails),
    bcc: listOf(attributes.bccEmails),
  };
}

/**
 * Does this message SAY who it went to, rather than leaving it to be inferred?
 *
 * The bubble needs this because "is this an email" is answered from the stored
 * html, and a reply can be email without any: an attachment-only send, or a
 * plain-text one. Asked of the recipient fields directly it stays false on every
 * other channel, where the contact is the only possible recipient and a "To"
 * line over each bubble would be noise.
 */
export function namesRecipients(message: Message): boolean {
  if (message.messageType === 'incoming') {
    const email = message.contentAttributes.email;
    return listOf(email?.to).length > 0 || listOf(email?.cc).length > 0;
  }
  const attributes = message.contentAttributes as Record<string, unknown>;
  return (
    listOf(attributes.toEmails).length > 0 ||
    listOf(attributes.ccEmails).length > 0 ||
    listOf(attributes.bccEmails).length > 0
  );
}

/** The subject this message carries, inbound header or forward override. */
export function subjectOf(message: Message): string {
  const own = message.contentAttributes.email?.subject;
  if (typeof own === 'string' && own.trim()) return own.trim();
  const override = (message.contentAttributes as Record<string, unknown>).subject;
  return typeof override === 'string' ? override.trim() : '';
}

/** The sender of an inbound mail — the From header, else nothing. */
export function senderOf(message: Message): string[] {
  return message.messageType === 'incoming' ? listOf(message.contentAttributes.email?.from) : [];
}

/**
 * Who an inbound mail asks to be answered at: its Reply-To, else its From.
 *
 * Not the same as `senderOf`. Mail sent through a form or a notification
 * service comes From `noreply@…` with the person in Reply-To — the server
 * already makes that person the contact (channels/email/parse.js) — and a
 * Reply all built from the From copied the noreply address on every answer.
 * The forward header still names the real From; that one is a record, not an
 * address to write to. Mail ingested before Reply-To was kept carries none, and
 * falls back to the From exactly as before.
 */
export function answerAddressOf(message: Message): string[] {
  if (message.messageType !== 'incoming') return [];
  const replyTo = listOf(message.contentAttributes.email?.replyTo);
  return replyTo.length ? replyTo : senderOf(message);
}

export interface ReplyTargets {
  /** The contact this thread belongs to; always the first To. */
  contactEmail: string | null;
  /** This inbox's own address — never copied back to itself. */
  ownEmail: string | null;
  /** The newest inbound message, whose header is what "all" means. */
  lastInbound: Message | undefined;
}

/**
 * What Reply and Reply all address.
 *
 * Reply is the contact and nobody else. Reply all adds everyone the last inbound
 * message reached — whoever SENT it (its Reply-To when it named one, see
 * answerAddressOf), its other To recipients and its Cc — minus
 * this mailbox and minus the contact, who is already on the To line. Our own
 * address has to go because a shared inbox is nearly always in the To of the
 * mail it received, and copying it back in makes every reply re-ingest as a new
 * inbound message.
 *
 * The SENDER is in that list because the newest inbound message is not always
 * from the contact this thread belongs to: a third party we copied replies to
 * all, their mail carries our Message-ID in its References, and it threads into
 * this same conversation under a contact of its own (channels/email/threading.js).
 * Reading only To and Cc drops the one person who just wrote — and `reachedMany`
 * below already counts the From, so the two would disagree about whether there
 * is anyone to copy.
 *
 * Bcc is never carried forward. A blind copy is a fact about the message it was
 * on; repeating it on the reply is how one gets un-blinded.
 */
export function replyRecipients(
  targets: ReplyTargets,
  mode: 'reply' | 'replyAll',
): { to: string[]; cc: string[] } {
  const to = targets.contactEmail ? [targets.contactEmail] : [];
  if (mode === 'reply') return { to, cc: [] };
  const inbound = targets.lastInbound;
  if (!inbound) return { to, cc: [] };
  const { to: theirTo, cc: theirCc } = recipientsOf(inbound);
  const spokenFor = [...to, ...(targets.ownEmail ? [targets.ownEmail] : [])];
  return { to, cc: dedupeAddresses([...answerAddressOf(inbound), ...theirTo, ...theirCc], spokenFor) };
}

/** Is there anyone a Reply all would add that a Reply would not? */
export function hasOthers(targets: ReplyTargets): boolean {
  return replyRecipients(targets, 'replyAll').cc.length > 0;
}

/**
 * Did this message reach more than one party — is there a Reply all to make at
 * all?
 *
 * Deliberately a different question from `hasOthers`, and asked of the RAW
 * header rather than of the reply we would build. `hasOthers` asks whether a
 * Reply all would ADD anybody, which is the right question for a button that
 * SENDS on the click: one that copies nobody is a second Send wearing another
 * word. This asks whether the mail went to more than one place, which is the
 * right question for a button that merely OPENS a composer, and it is the rule
 * every mail client uses.
 *
 * The difference is not academic. A shared mailbox is routinely one of the
 * addressees, so a mail written to the customer AND to us has exactly one
 * "other" party, we remove ourselves, and nothing is left to add — no Reply all
 * offered, on a message whose header plainly lists two addresses. That reads as
 * a missing button, and it is what this fixes.
 */
export function reachedMany(message: Message | undefined): boolean {
  if (!message) return false;
  const { to, cc } = recipientsOf(message);
  return dedupeAddresses([...answerAddressOf(message), ...to, ...cc]).length > 1;
}

const stripPrefixes = (subject: string): string =>
  subject.replace(/^(\s*(re|fwd?|aw|wg)\s*:\s*)+/i, '').trim();

/** `Fwd: <subject>`, without stacking a second prefix on one already there. */
export function forwardSubject(subject: string): string {
  const bare = stripPrefixes(subject);
  return bare ? `Fwd: ${bare}` : 'Fwd:';
}

/** True when two subjects are the same thread — prefixes and case aside. */
export const sameSubject = (a: string, b: string): boolean =>
  stripPrefixes(a).toLowerCase() === stripPrefixes(b).toLowerCase();

/**
 * The line that opens the quoted original a forward carries.
 *
 * Exported because Cancel takes that quote back OUT (see Composer's
 * cancelForward) and it has to find where the composer put it — a second copy
 * of this string would leave the customer's own message sitting in a reply
 * addressed back to them the first time either was reworded.
 */
export const FORWARD_QUOTE_MARKER = '---------- Forwarded message ----------';

/** What was written ABOVE a forward's quote, with the quote itself gone. */
export function withoutForwardQuote(body: string): string {
  const at = body.indexOf(FORWARD_QUOTE_MARKER);
  return at === -1 ? body : body.slice(0, at).replace(/\s+$/, '');
}

/**
 * The quoted original a forward carries, as plain text.
 *
 * Text, not the sender's html: the forwarded body is pasted into an editor the
 * agent then writes above, and dropping arbitrary mail markup into a
 * contenteditable brings its css, its tables and its tracking pixels along with
 * it. What matters on a forward is the header block — who sent it, when, to
 * whom — and the words.
 */
export function forwardQuote(
  message: Message,
  { fallbackTo, sentAt }: { fallbackTo?: string | null; sentAt: string },
): string {
  const { to, cc } = recipientsOf(message, fallbackTo);
  const from = senderOf(message);
  const lines = [FORWARD_QUOTE_MARKER];
  if (from.length) lines.push(`From: ${from.join(', ')}`);
  lines.push(`Date: ${sentAt}`);
  const subject = subjectOf(message);
  if (subject) lines.push(`Subject: ${subject}`);
  if (to.length) lines.push(`To: ${to.join(', ')}`);
  if (cc.length) lines.push(`Cc: ${cc.join(', ')}`);
  lines.push('', message.content?.trim() ?? '');
  return lines.join('\n');
}

/** The contact address a thread replies to, or null when it has none. */
export const contactEmailOf = (conversation: Conversation): string | null =>
  conversation.contact?.email?.trim() || null;
